import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getHourConditionsTranslateKeys } from '../../../../../../store/pricingDucks/siteProducts';

import { FlightType } from './FlightType';

const Wrapper = styled.div`
  padding: 20px 27px 20px 22px;
  background-color: #fff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.3);
  border-radius: 3px;
  border-left: 5px solid transparent;
  margin-bottom: 10px;

  table {
    @media (max-width: 768px) {
      margin: 0 auto;
    }
  }

  td {
    padding: 0 13px 11px 0;

    @media (max-width: 768px) {
      padding: 0 4px 11px 0;
    }
  }
`;

const TdFlightType = styled.td`
  color: #333333;
  font-size: 16px;
  line-height: 21px;
`;

const TdFlightTypeDesktop = styled(TdFlightType)`
  vertical-align: middle;
  @media (max-width: 768px) {
    display: none;
  }
`;

const ThEmpty = styled.th`
  @media (max-width: 768px) {
    display: none;
  }
`;

const TdEmpty = styled.td`
  @media (max-width: 768px) {
    display: none;
  }
`;

const ThHourCondition = styled.td`
  color: #91abb5;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-align: center;
  min-width: 115px;
  @media (max-width: 768px) {
    min-width: 94px;
  }
`;

const TrMobile = styled.tr`
  @media (min-width: 769px) {
    display: none;
  }
`;

const ActiveOrientedResetBtnStyles = css`
  background-color: ${(props) =>
    props.isActive ? props.colorStyle : 'rgba(255,255,255,0.8)'};
  color: ${(props) => (props.isActive ? '#fff' : '#000')};
`;

const ResetFlightTypeBtn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Barlow Condensed Extra Bold', sans-serif;
  font-weight: 800;
  font-size: 19px;
  line-height: 23px;
  height: 40px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 1.64px;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.17);
  cursor: pointer;
  ${ActiveOrientedResetBtnStyles}
  :hover {
    background-color: ${(props) => props.colorStyle};
    color: #fff;
    @media (max-width: 768px) {
      // overwriting hover styles on mobile
      ${ActiveOrientedResetBtnStyles}
    }
  }
`;

export const FlightTypeSelector = (props) => {
  const { onCarbonOffsetChange, carbonOffset, activeColorStyle } = props;
  const { t } = useTranslation();
  const carbonOffsetInputs = useSelector(
    (state) =>
      state.siteProducts.data?.carbonOffsetForm?.carbonOffsetInputs || [],
  );
  const hourConditionsTranslateKeys = useSelector(
    getHourConditionsTranslateKeys,
  );

  return (
    <Wrapper>
      <table>
        <thead>
          <tr>
            <ThEmpty />
            {hourConditionsTranslateKeys.map((hourConditionKey) => (
              <ThHourCondition key={hourConditionKey}>
                {t(`site.purchase.select-package.carbon.${hourConditionKey}`)}
              </ThHourCondition>
            ))}
          </tr>
        </thead>

        <tbody>
          {carbonOffsetInputs.map((rowInputs) => (
            <React.Fragment key={rowInputs.name}>
              <TrMobile>
                <TdFlightType colSpan="3">
                  {t(
                    `site.purchase.select-package.carbon.${rowInputs.i18nKey}`,
                  )}
                </TdFlightType>
              </TrMobile>

              <tr>
                <TdFlightTypeDesktop>
                  {t(
                    `site.purchase.select-package.carbon.${rowInputs.i18nKey}`,
                  )}
                </TdFlightTypeDesktop>
                {rowInputs.options.map((option) => (
                  <td key={option.name} data-testid="selector-option">
                    <FlightType
                      onCarbonOffsetChange={onCarbonOffsetChange}
                      carbonOffset={carbonOffset}
                      name={option.name}
                      value={option.value}
                      activeColorStyle={activeColorStyle}
                    />
                  </td>
                ))}
              </tr>
            </React.Fragment>
          ))}

          <tr>
            <TdEmpty />
            <td colSpan="3">
              <ResetFlightTypeBtn
                isActive={carbonOffset === '0'}
                onClick={() => onCarbonOffsetChange('0')}
                colorStyle={activeColorStyle}
                data-testid="reset-button"
              >
                {t('site.purchase.select-package.carbon.no-thanks')}
              </ResetFlightTypeBtn>
            </td>
          </tr>
        </tbody>
      </table>
    </Wrapper>
  );
};

FlightTypeSelector.propTypes = {
  onCarbonOffsetChange: PropTypes.func,
  carbonOffset: PropTypes.string,
  activeColorStyle: PropTypes.string,
};
